import type { RefObject } from "react";
import type { TaskModel } from "@bryntum/gantt-thin";
import { createProjectTask, rawRequestToTask } from "./gantt-utils";
import type { GanttProject } from "./prop-types";

interface TearsheetCallbackData {
   updatedProject: GanttProject;
   requests?: Array<{
      subcategory_id?: string;
      category_id?: string;
   }>;
   jobTitles?: any;
}

export interface GanttInstance {
   instance: {
      crudManager: {
         suspendAutoSync: () => Promise<void>;
         resumeAutoSync: () => Promise<void>;
         acceptChanges: () => Promise<void>;
      };
      taskStore: any;
      addSubtask: any;
   };
}

export const handleProjectTearsheetCallback = async (
   data: TearsheetCallbackData,
   ganttRef: RefObject<GanttInstance>,
   expandedTasks: any,
   groupId: string,
): Promise<void> => {
   const { updatedProject, requests, jobTitles } = data;

   await ganttRef.current?.instance.crudManager.suspendAutoSync();
   try {
      if (updatedProject.group_ids.includes(groupId) || groupId === "my-groups") {
         const taskFromStore = ganttRef.current?.instance.taskStore.getById(updatedProject.id);

         if (!updatedProject.est_end_date) {
            updatedProject.est_end_date = taskFromStore?.getData("endDate");
         }

         taskFromStore?.set(createProjectTask(updatedProject, expandedTasks));

         if (requests) {
            await addRequestsToTaskStore(requests, updatedProject, jobTitles, ganttRef);
         }
      } else {
         ganttRef.current?.instance.taskStore.remove(updatedProject.id);
      }

      await ganttRef.current?.instance.crudManager.acceptChanges();
   } finally {
      await ganttRef.current?.instance.crudManager.resumeAutoSync();
   }
};

const addRequestsToTaskStore = async (
   requests: TearsheetCallbackData["requests"],
   updatedProject: GanttProject,
   jobTitles: any,
   ganttRef: RefObject<GanttInstance>,
): Promise<void> => {
   if (!requests) return;

   for (const request of requests) {
      const requestTask = rawRequestToTask({
         request,
         project: updatedProject,
         jobTitles,
      });

      let parentTaskId: string;

      if (request.subcategory_id) {
         parentTaskId = request.subcategory_id;
      } else if (request.category_id) {
         parentTaskId = request.category_id;
      } else {
         parentTaskId = updatedProject.id;
      }

      const parentTask = ganttRef.current?.instance.taskStore.getById(parentTaskId);
      const newTaskRecord = await ganttRef.current?.instance.addSubtask(parentTask as TaskModel, {
         data: requestTask,
      });

      await newTaskRecord?.setEndDate(new Date(requestTask!.endDate!));
   }
};
